// Curtain / blind. Commands: OPENCURTAIN / CLOSECURTAIN / STOPCURTAIN
// (uppercase, matching the Flutter app / firmware).
//
// A press is a TIMED RUN — the motor is momentary. The clock, the reversal rule
// and the "a repeat is a stop at the device" rule all live in
// core/devices/curtainRunner, shared with the mobile screen and the grid card.
// See core/devices/curtain.js for where the seconds are stored.

import React, { useState } from 'react';
import {
  Stack, Button, Box, Typography, LinearProgress, IconButton, Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StopIcon from '@mui/icons-material/Stop';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TimerIcon from '@mui/icons-material/Timer';
import DeviceShell from '../../components/DeviceShell';
import useCurtain from '../../../../core/hooks/useCurtain';
import CurtainTimesDialog from '../../../shared/CurtainTimesDialog';

export default function CurtainScreen({ device }) {
  const uno = device.UniqueNumber;
  const curtain = useCurtain(device);
  const [editTimes, setEditTimes] = useState(false);

  return (
    <DeviceShell title={device.DeviceName || uno}>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {`Open ${curtain.times.openSeconds}s · Close ${curtain.times.closeSeconds}s`}
          </Typography>
          <Tooltip title="How long the motor runs for">
            <IconButton size="small" onClick={() => setEditTimes(true)}>
              <TimerIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Reserved height so the buttons do not jump when a run starts. */}
        <Box sx={{ minHeight: 40 }}>
          {curtain.moving && (
            <>
              <LinearProgress variant="determinate" value={curtain.progress} sx={{ height: 6, borderRadius: 3 }} />
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {`${curtain.opening ? 'Opening' : 'Closing'} — ${curtain.remainingSeconds}s left`}
              </Typography>
            </>
          )}
        </Box>

        {/* ⚠️ DISABLED WHILE ITS OWN DIRECTION RUNS. A repeat of the same command
            is a STOP at the device (curtainRunner rule 3), so the button must
            not send — and greying it says so rather than leaving a press that
            silently does nothing. The opposite one stays live: pressing it
            reverses. */}
        <Button
          variant="contained" size="large" startIcon={<ArrowUpwardIcon />}
          sx={{ py: 2 }} disabled={curtain.opening} onClick={curtain.open}
        >
          {curtain.labels.open}
        </Button>
        <Button
          variant={curtain.moving ? 'contained' : 'outlined'}
          color={curtain.moving ? 'warning' : 'primary'}
          size="large" startIcon={<StopIcon />}
          sx={{ py: 2 }} onClick={curtain.stop}
        >
          {curtain.labels.stop}
        </Button>
        <Button
          variant="contained" size="large" startIcon={<ArrowDownwardIcon />}
          sx={{ py: 2 }} disabled={curtain.closing} onClick={curtain.close}
        >
          {curtain.labels.close}
        </Button>
      </Stack>

      <CurtainTimesDialog
        open={editTimes}
        onClose={() => setEditTimes(false)}
        curtain={curtain}
        deviceName={device.DeviceName}
      />
    </DeviceShell>
  );
}
